// Loan-level DTI for V2 loans: the header, the calculator, the Rates tab and the
// header rate picker all go through here so they show the same numbers.
import * as com from "../../Common"
import { buildDTIApplicationV2 } from "./dtiApplicationV2"
import { computeDTI, defaultProgram } from "./dtiEngine"
import { buildEstimatorInitialValues, estimatePayment, subjectExpenses1003 } from "./dtiPayment"

export const initialSettings = (application, settings) => ({
    program: settings?.program || "",
    underwriting: settings?.underwriting || "manual",
    excluded: settings?.excluded || [],
    pendingSaleContract: settings?.pendingSaleContract || [],
    exclusionReasons: settings?.exclusionReasons || {},
    estimator: settings?.estimator || null,
    autoProgram: defaultProgram(application),
})

const engineOptions = (application, settings) => {
    const setup = initialSettings(application, settings)
    return {
        program: setup.program || setup.autoProgram,
        underwriting: setup.underwriting,
        excluded: setup.excluded,
        pendingSaleContract: setup.pendingSaleContract,
    }
}

/** Header / calculator DTI with the estimator's subject payment. */
export const computeLoanDTI = (application, legacyView, settings, payment) => {
    const options = engineOptions(application, settings)
    const estimatorValues = buildEstimatorInitialValues(legacyView, com.getCustomerId(), options.program, settings?.estimator)
    const estimate = estimatePayment(estimatorValues)
    return computeDTI(application, {
        ...options,
        payment: payment || { total: estimate.total, breakdown: estimate.breakdown, isEstimate: estimatorValues.rateSource === "default" },
    })
}

// Pricing run of the engine: the subject's taxes, insurance and homeowners association
// dues in, its principal and interest and mortgage insurance out.
const pricingDTI = (application, settings, ignoreEstimator) => {
    const legacyView = buildDTIApplicationV2(application, {})
    const options = engineOptions(application, settings)
    const values = buildEstimatorInitialValues(legacyView, com.getCustomerId(), options.program, ignoreEstimator ? null : settings?.estimator)
    const { breakdown } = estimatePayment(values)
    const result = computeDTI(application, {
        ...options,
        pricing: true,
        payment: { total: breakdown.propertyTaxes + breakdown.insurance + breakdown.hoaFees },
    })
    return { legacyView, values, result }
}

/**
 * Monthly income and debts for pricing (Rates tab, header rate picker). The
 * pricing engine computes DTI as (debts + product P&I + MI) / income, so the
 * subject's P&I and MI stay out of the debts here; its taxes, insurance and HOA
 * stay in (they used to be missing on purchases, and a selected loan's payment
 * used to be counted on top of the product's).
 *
 * ignoreEstimator: the 1003's taxes, insurance and homeowners association dues
 * instead of the ones saved in the calculator's estimated payment
 * (settings.estimator); the program and the debts the loan officer unchecked in
 * the calculator still apply. For the Rates tab's "Reset to 1003 values".
 */
export const computePricingInputs = (application, settings, { ignoreEstimator = false } = {}) => {
    if (!application) return null
    const { result } = pricingDTI(application, settings, ignoreEstimator)
    return { income: Math.round(result.totals.income), liabilities: Math.round(result.totals.obligations) }
}

const num = (value) => {
    const parsed = parseFloat(`${value ?? ""}`.replace(/[,\s$]/g, ""))
    return Number.isFinite(parsed) ? parsed : 0
}
const round2 = (value) => Math.round(value * 100) / 100

export const SUBJECT_EXPENSE_LABELS = { propertyTaxes: "Property taxes", insurance: "Homeowner's insurance", hoaFees: "Homeowners association dues" }
const SUBJECT_EXPENSES = Object.keys(SUBJECT_EXPENSE_LABELS)
// The other debts the engine counts, by row category, in display order.
const DEBT_GROUPS = [
    { category: "property", label: "Other properties owned" },
    { category: "rent", label: "Current rent" },
    { category: "obligation", label: "Alimony, child support and other obligations" },
]

/**
 * What computePricingInputs' liabilities are made of, for the Rates tab's hint.
 * lines: { key, label, amount, source: "1003" | "calculator", count? } — credit
 * report debts, the subject's taxes / insurance / homeowners association dues (each
 * from the 1003 or the calculator's estimated payment), then anything else the
 * engine counts; they add up to total. excluded: debts the loan officer unchecked
 * in the calculator. mortgageInsurance1003: the 1003's mortgage insurance, left out
 * because pricing adds each product's own (null when the 1003 only has a combined
 * total). unusedCalculatorValues: with ignoreEstimator, the calculator amounts not used.
 */
export const computePricingLiabilities = (application, settings, { ignoreEstimator = false } = {}) => {
    if (!application) return null
    const { legacyView, values, result } = pricingDTI(application, settings, ignoreEstimator)
    const rows = [...result.housing, ...result.debts]
    const counted = rows.filter((row) => !row.excluded)
    const overridden = new Set(values.overridden || [])
    const expenses1003 = subjectExpenses1003(legacyView.property)
    const lines = []
    const addGroup = (category, label) => {
        const group = counted.filter((row) => row.category === category)
        if (!group.length) return
        lines.push({ key: category, label, count: group.length, amount: round2(group.reduce((total, row) => total + row.monthly, 0)), source: "1003" })
    }

    addGroup("tradeline", "Credit report debts")
    const subjectExcluded = rows.some((row) => row.id === "subject:pitia" && row.excluded)
    if (!subjectExcluded) {
        SUBJECT_EXPENSES.forEach((key) => {
            const source = overridden.has(key) ? "calculator" : "1003"
            // A combined 1003 total sits in insurance; taxes and dues are then 0, not missing.
            if (source === "1003" && expenses1003.combined && key !== "insurance") return
            const label = key === "insurance" && source === "1003" && expenses1003.combined
                ? "Taxes, insurance and homeowners association dues (1003 total)"
                : SUBJECT_EXPENSE_LABELS[key]
            lines.push({ key, label, amount: num(values[key]), source })
        })
    }
    counted
        .filter((row) => row.category === "subject" && row.id !== "subject:pitia")
        .forEach((row) => lines.push({ key: row.id, label: row.label, amount: row.monthly, source: "1003" }))
    DEBT_GROUPS.forEach(({ category, label }) => addGroup(category, label))
    const listed = new Set(["tradeline", "subject", ...DEBT_GROUPS.map((group) => group.category)])
    counted
        .filter((row) => !listed.has(row.category))
        .forEach((row) => lines.push({ key: row.id, label: row.label, amount: row.monthly, source: "1003" }))

    const excludedRows = rows.filter((row) => row.excluded && row.monthly)
    const saved = settings?.estimator || {}
    const unusedCalculatorValues = ignoreEstimator
        ? SUBJECT_EXPENSES
            .filter((key) => saved[key] !== undefined && saved[key] !== null && num(saved[key]) !== num(values[key]))
            .map((key) => ({ key, label: SUBJECT_EXPENSE_LABELS[key], amount: num(saved[key]) }))
        : []
    return {
        total: Math.round(result.totals.obligations),
        lines,
        excluded: { count: excludedRows.length, amount: round2(excludedRows.reduce((total, row) => total + row.monthly, 0)) },
        mortgageInsurance1003: expenses1003.combined ? null : expenses1003.pmi,
        unusedCalculatorValues,
    }
}
